import type { TripItem } from '@shared/types';
import { TYPES_WITHOUT_IMAGES } from '@shared/types';
import { faviconFor, hostnameOf } from '../lib/format';

interface Props {
  item: TripItem;
  onEdit?: () => void;
  onDelete?: () => void;
}

/**
 * Card shape follows the item's kind rather than one uniform row.
 *
 * A tip has no place, no photo, and no link — rendering it in the same
 * image-plus-note frame as a hotel gives it a blank square and false weight.
 * Tips render as list rows instead; everything else keeps the photo card.
 */
export function ItemCard(props: Props) {
  return TYPES_WITHOUT_IMAGES.includes(props.item.type) ? (
    <TipRow {...props} />
  ) : (
    <PlaceCard {...props} />
  );
}

function ActionButtons({ onEdit, onDelete }: Pick<Props, 'onEdit' | 'onDelete'>) {
  if (!onEdit && !onDelete) return null;
  return (
    <div className="mt-2 flex gap-1">
      {onEdit && (
        <button onClick={onEdit} className="btn-quiet !px-2 !py-1 !text-meta">
          Edit
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} className="btn-quiet !px-2 !py-1 !text-meta">
          Remove
        </button>
      )}
    </div>
  );
}

/** Tips: a compact row with a drawn bullet. No image, no frame. */
function TipRow({ item, onEdit, onDelete }: Props) {
  const heading = item.ogTitle?.trim();
  return (
    <div className="flex gap-3 py-3">
      <span aria-hidden="true" className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-accent" />
      <div className="min-w-0 flex-1">
        {heading && (
          <p className="font-util text-[0.9375rem] font-semibold text-ink">{heading}</p>
        )}
        {item.note && (
          <p className={`font-body text-note text-ink/85 ${heading ? 'mt-0.5' : ''}`}>
            {item.note}
          </p>
        )}
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-block font-util text-meta text-accent underline"
          >
            {hostnameOf(item.url)}
          </a>
        )}
        <ActionButtons onEdit={onEdit} onDelete={onDelete} />
      </div>
    </div>
  );
}

/** Lodging, restaurants, activities: photo, name, source, note, map link. */
function PlaceCard({ item, onEdit, onDelete }: Props) {
  const host = hostnameOf(item.url);
  const favicon = faviconFor(item.url);
  const title = item.ogTitle?.trim() || item.placeName || host || 'Untitled';
  const pending = item.unfurlStatus === 'pending';

  return (
    <article className="py-6 first:pt-0">
      {item.ogImage && (
        <img
          src={item.ogImage}
          alt=""
          loading="lazy"
          className="mb-3 aspect-[16/9] w-full rounded-img object-cover bg-raised"
        />
      )}

      {/* Title, source, and note read as one block under the photo. */}
      <h3 className="font-util text-place font-semibold text-ink">
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-accent transition-colors"
          >
            {title}
          </a>
        ) : (
          title
        )}
      </h3>

      {item.placeAddress && (
        <p className="mt-0.5 font-util text-meta text-muted">{item.placeAddress}</p>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        {host && (
          <span className="flex items-center gap-1.5 font-util text-meta text-muted">
            {favicon && <img src={favicon} alt="" className="h-3.5 w-3.5" />}
            <span className="truncate">{host}</span>
          </span>
        )}
        {item.mapUrl && (
          <a
            href={item.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-util text-meta text-accent underline"
          >
            Open in Maps
          </a>
        )}
        {item.unfurlStatus === 'failed' && !item.ogImage && (
          <span className="font-util text-meta text-muted/80">no preview available</span>
        )}
      </div>

      {pending && <p className="mt-1 font-util text-meta text-muted">Loading preview…</p>}

      {item.note && <p className="note-aside mt-3">{item.note}</p>}

      <ActionButtons onEdit={onEdit} onDelete={onDelete} />
    </article>
  );
}
